import React, { useEffect, useMemo, useState } from "react";
import {
  CalculatorOutlined,
  CheckCircleOutlined,
  DownOutlined,
  InfoCircleOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Alert, Checkbox, InputNumber, Select, Spin, Tooltip } from "antd";
import axios from "axios";

import { resolveFrontendApiBase } from "../utils/apiBase";
import { PROOFREADING_WORD_COST_MULTIPLIER } from "../utils/proofreadingPricing";
import { recordUxEvent } from "../utils/uxTelemetry";

type PurchaseOption = {
  key: string;
  name: string;
  credits: number;
  price: number;
  kind: "subscription" | "forever";
  billingInterval?: "monthly" | "yearly";
};

const API_BASE = resolveFrontendApiBase();
const GLOSSARY_WORD_COST_MULTIPLIER = 0.1;
const BOOK_TRANSLATION_RATE_LOW = 0.06;
const BOOK_TRANSLATION_RATE_HIGH = 0.12;
const BOOK_TRANSLATION_RATES_URL =
  "https://reedsy.com/translation/book-translator";

const firstFiniteNumber = (...values: unknown[]): number => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
};

const normalizePurchaseOption = (
  rawPlan: any,
  kind: PurchaseOption["kind"]
): PurchaseOption | null => {
  const metadata =
    rawPlan?.metadata && typeof rawPlan.metadata === "object"
      ? rawPlan.metadata
      : {};
  const rawInterval = String(
    rawPlan?.billingInterval ||
      rawPlan?.interval ||
      metadata.billingInterval ||
      "monthly"
  )
    .trim()
    .toLowerCase();
  const billingInterval: PurchaseOption["billingInterval"] =
    rawInterval.startsWith("year") || rawInterval.startsWith("annual")
      ? "yearly"
      : "monthly";
  const credits =
    kind === "forever"
      ? firstFiniteNumber(
          rawPlan?.oneTimeCredits,
          rawPlan?.credits,
          rawPlan?.creditsPerInterval,
          metadata.oneTimeCredits,
          metadata.credits,
          metadata.creditsPerInterval
        )
      : firstFiniteNumber(
          rawPlan?.monthlyCredits,
          rawPlan?.creditsPerInterval,
          rawPlan?.monthlyEquivalentCredits,
          metadata.monthlyCredits,
          metadata.creditsPerInterval,
          metadata.monthlyEquivalentCredits
        );
  const price = firstFiniteNumber(
    rawPlan?.price,
    rawPlan?.amount,
    metadata.price
  );
  if (!credits || !price) return null;
  const name = String(
    rawPlan?.displayName ||
      rawPlan?.planName ||
      metadata.displayName ||
      rawPlan?.planKey ||
      "Credit option"
  ).trim();
  return {
    key: String(
      rawPlan?.id || rawPlan?.planKey || `${kind}-${name}-${credits}`
    ),
    name,
    credits,
    price,
    kind,
    billingInterval: kind === "subscription" ? billingInterval : undefined,
  };
};

const ProjectCostEstimator: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [wordCount, setWordCount] = useState(95000);
  const [targetLanguageCount, setTargetLanguageCount] = useState(1);
  const [includeProofreading, setIncludeProofreading] = useState(true);
  const [includeGlossaryScan, setIncludeGlossaryScan] = useState(false);
  const [purchaseOptions, setPurchaseOptions] = useState<PurchaseOption[]>([]);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingUnavailable, setPricingUnavailable] = useState(false);

  useEffect(() => {
    recordUxEvent("project_cost_estimator_viewed");
    let cancelled = false;
    const loadPricing = async () => {
      setPricingLoading(true);
      setPricingUnavailable(false);
      try {
        const response = await axios.get(`${API_BASE}/get-subscription-plans`, {
          params: {
            includeInactive: false,
            includeLegacy: false,
            includeSubscriberOnly: false,
          },
        });
        if (cancelled) return;
        const subscriptionOptions = (response.data?.subscriptionPlans || [])
          .map((plan: any) => normalizePurchaseOption(plan, "subscription"))
          .filter(Boolean) as PurchaseOption[];
        const foreverOptions = (response.data?.foreverCreditPlans || [])
          .map((plan: any) => normalizePurchaseOption(plan, "forever"))
          .filter(Boolean) as PurchaseOption[];
        setPurchaseOptions([...subscriptionOptions, ...foreverOptions]);
        setPricingUnavailable(
          subscriptionOptions.length + foreverOptions.length === 0
        );
        recordUxEvent("project_cost_estimator_pricing_loaded", {
          option_count: subscriptionOptions.length + foreverOptions.length,
        });
      } catch (_error) {
        if (!cancelled) {
          setPurchaseOptions([]);
          setPricingUnavailable(true);
        }
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    };
    void loadPricing();
    return () => {
      cancelled = true;
    };
  }, []);

  const estimate = useMemo(() => {
    const safeWords = Math.max(0, Math.round(wordCount || 0));
    const safeLanguages = Math.max(1, Math.round(targetLanguageCount || 1));
    const translationCredits = safeWords * safeLanguages;
    const proofreadingCredits = includeProofreading
      ? Math.ceil(safeWords * PROOFREADING_WORD_COST_MULTIPLIER * safeLanguages)
      : 0;
    const glossaryCredits = includeGlossaryScan
      ? Math.ceil(safeWords * GLOSSARY_WORD_COST_MULTIPLIER * safeLanguages)
      : 0;
    return {
      translationCredits,
      proofreadingCredits,
      glossaryCredits,
      totalCredits: translationCredits + proofreadingCredits + glossaryCredits,
      traditionalTranslationLow:
        safeWords * safeLanguages * BOOK_TRANSLATION_RATE_LOW,
      traditionalTranslationHigh:
        safeWords * safeLanguages * BOOK_TRANSLATION_RATE_HIGH,
    };
  }, [
    includeGlossaryScan,
    includeProofreading,
    targetLanguageCount,
    wordCount,
  ]);

  const monthlyEstimate = useMemo(() => {
    if (!estimate.totalCredits || !purchaseOptions.length) return null;
    const monthlyOptions = purchaseOptions.filter(
      (option) =>
        option.kind === "subscription" && option.billingInterval === "monthly"
    );
    if (!monthlyOptions.length) return null;

    const highestMonthlyPlan = [...monthlyOptions].sort(
      (left, right) => right.credits - left.credits || left.price - right.price
    )[0];
    const oneMonthPlan = monthlyOptions
      .filter((option) => option.credits >= estimate.totalCredits)
      .sort(
        (left, right) =>
          left.price - right.price || left.credits - right.credits
      )[0];
    const option = oneMonthPlan || highestMonthlyPlan;
    const months = Math.ceil(estimate.totalCredits / option.credits);
    return {
      ...option,
      months,
      totalPrice: Number((option.price * months).toFixed(2)),
      coveredCredits: option.credits * months,
      isMultiMonth: months > 1,
    };
  }, [estimate.totalCredits, purchaseOptions]);

  const foreverEstimate = useMemo(() => {
    if (!estimate.totalCredits || !purchaseOptions.length) return null;
    const candidates = purchaseOptions
      .filter((option) => option.kind === "forever")
      .map((option) => {
        const quantity = Math.ceil(estimate.totalCredits / option.credits);
        return {
          ...option,
          quantity,
          totalPrice: Number((option.price * quantity).toFixed(2)),
          coveredCredits: option.credits * quantity,
        };
      });
    return (
      candidates.sort((left, right) => left.totalPrice - right.totalPrice)[0] ||
      null
    );
  }, [estimate.totalCredits, purchaseOptions]);

  return (
    <section
      className={`project-cost-estimator${
        isExpanded ? " project-cost-estimator--expanded" : ""
      }`}
      data-tour-id="pricing-cost-estimator"
      aria-labelledby="project-cost-estimator-title"
    >
      <button
        type="button"
        className="project-cost-estimator__header"
        aria-expanded={isExpanded}
        aria-controls="project-cost-estimator-content"
        onClick={() => {
          const nextExpanded = !isExpanded;
          setIsExpanded(nextExpanded);
          recordUxEvent("project_cost_estimator_toggled", {
            expanded: nextExpanded,
          });
        }}
      >
        <div className="project-cost-estimator__icon" aria-hidden="true">
          <CalculatorOutlined />
        </div>
        <div className="project-cost-estimator__header-copy">
          <h2 id="project-cost-estimator-title">Estimate your project cost</h2>
          <p>
            Plan with an approximate word count—no upload or account required.
          </p>
        </div>
        <span className="project-cost-estimator__toggle-label">
          {isExpanded ? "Hide estimator" : "Open estimator"}
        </span>
        <DownOutlined
          className="project-cost-estimator__chevron"
          aria-hidden="true"
        />
      </button>
      {isExpanded && (
        <div
          id="project-cost-estimator-content"
          className="project-cost-estimator__content"
        >
          <div className="project-cost-estimator__grid">
            <label>
              <span>Manuscript word count</span>
              <InputNumber
                aria-label="Manuscript word count"
                min={0}
                step={5000}
                value={wordCount}
                onChange={(value) =>
                  setWordCount(Math.max(0, Number(value) || 0))
                }
                onBlur={() =>
                  recordUxEvent("project_cost_estimator_updated", {
                    word_count_band:
                      Math.ceil(Math.max(0, wordCount) / 25000) * 25000,
                    target_language_count: targetLanguageCount,
                  })
                }
              />
            </label>
            <label>
              <span>Number of target languages</span>
              <Select
                aria-label="Number of target languages"
                value={targetLanguageCount}
                onChange={setTargetLanguageCount}
                options={[1, 2, 3, 4, 5].map((value) => ({
                  value,
                  label: `${value} language${value === 1 ? "" : "s"}`,
                }))}
              />
            </label>
            <div
              className="project-cost-estimator__options"
              aria-label="Optional workflow steps"
            >
              <div className="project-cost-estimator__option-toggle">
                <Checkbox
                  checked={includeProofreading}
                  onChange={(event) => {
                    setIncludeProofreading(event.target.checked);
                    recordUxEvent("project_cost_estimator_option_changed", {
                      option: "proofreading",
                      enabled: event.target.checked,
                    });
                  }}
                >
                  Standard proofreading
                </Checkbox>
                <Tooltip title="Runs an AI review after translation to suggest grammar, clarity, and consistency changes. You choose which changes to accept.">
                  <button
                    type="button"
                    className="project-cost-estimator__info"
                    aria-label="What does standard proofreading include?"
                  >
                    <InfoCircleOutlined />
                  </button>
                </Tooltip>
              </div>
              <div className="project-cost-estimator__option-toggle">
                <Checkbox
                  checked={includeGlossaryScan}
                  onChange={(event) => {
                    setIncludeGlossaryScan(event.target.checked);
                    recordUxEvent("project_cost_estimator_option_changed", {
                      option: "glossary_scan",
                      enabled: event.target.checked,
                    });
                  }}
                >
                  AI Glossary Scan
                </Checkbox>
                <Tooltip title="Scans before translation for names, places, and recurring terms that may need consistent treatment. You review suggestions before applying them.">
                  <button
                    type="button"
                    className="project-cost-estimator__info"
                    aria-label="What does the AI Glossary Scan include?"
                  >
                    <InfoCircleOutlined />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="project-cost-estimator__result" aria-live="polite">
            <div>
              <span>Estimated credits required</span>
              <strong>{estimate.totalCredits.toLocaleString()} credits</strong>
            </div>
            <div className="project-cost-estimator__breakdown">
              <span>
                Translation: {estimate.translationCredits.toLocaleString()}
              </span>
              {includeProofreading && (
                <span>
                  Proofreading: {estimate.proofreadingCredits.toLocaleString()}
                </span>
              )}
              {includeGlossaryScan && (
                <span>
                  Glossary scan: {estimate.glossaryCredits.toLocaleString()}
                </span>
              )}
            </div>
            {pricingLoading ? (
              <div className="project-cost-estimator__purchase">
                <Spin size="small" /> Checking current purchase options…
              </div>
            ) : (
              <div
                className="project-cost-estimator__purchase-options"
                aria-label="Purchase planning options"
              >
                {monthlyEstimate && (
                  <div className="project-cost-estimator__purchase-option">
                    <div className="project-cost-estimator__purchase-option-heading">
                      <CheckCircleOutlined />
                      <span>
                        {monthlyEstimate.isMultiMonth
                          ? `Spread over ${monthlyEstimate.months} months`
                          : "Monthly plan option"}
                      </span>
                    </div>
                    <strong>{monthlyEstimate.name}</strong>
                    <span>
                      {monthlyEstimate.credits.toLocaleString()} credits/month ·
                      ${monthlyEstimate.price.toFixed(2)}/month
                    </span>
                    {monthlyEstimate.isMultiMonth && (
                      <span>
                        Approximately{" "}
                        <strong>
                          ${monthlyEstimate.totalPrice.toFixed(2)} total
                        </strong>{" "}
                        across {monthlyEstimate.months} credit drops.
                      </span>
                    )}
                  </div>
                )}
                {foreverEstimate && (
                  <div className="project-cost-estimator__purchase-option">
                    <div className="project-cost-estimator__purchase-option-heading">
                      <CheckCircleOutlined />
                      <span>Forever Credits option</span>
                    </div>
                    <strong>
                      {foreverEstimate.name}
                      {foreverEstimate.quantity > 1
                        ? ` × ${foreverEstimate.quantity}`
                        : ""}
                    </strong>
                    <span>
                      {foreverEstimate.coveredCredits.toLocaleString()} credits
                      · approximately{" "}
                      <strong>${foreverEstimate.totalPrice.toFixed(2)}</strong>
                    </span>
                  </div>
                )}
                {pricingUnavailable && (
                  <Alert
                    className="project-cost-estimator__pricing-unavailable"
                    type="info"
                    showIcon
                    message="Current ScribeShadow dollar pricing is temporarily unavailable. Credit and professional-translator estimates are still usable."
                  />
                )}
                <span className="project-cost-estimator__pricing-disclaimer">
                  Planning comparison only. Professional rates vary by language
                  pair, expertise, turnaround, and included editing.
                  ScribeShadow prices are before taxes or subscriber discounts;
                  monthly estimates assume no credits are used on other work.
                </span>
              </div>
            )}
          </div>
          <aside
            className="project-cost-estimator__external-comparison"
            aria-label="External professional translation comparison"
          >
            <div className="project-cost-estimator__external-heading">
              <TeamOutlined aria-hidden="true" />
              <div>
                <span>External comparison</span>
                <strong>Hiring a professional translator</strong>
              </div>
            </div>
            <div className="project-cost-estimator__external-price">
              <strong>
                $
                {estimate.traditionalTranslationLow.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
                {"–"}$
                {estimate.traditionalTranslationHigh.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
              </strong>
              <span>Not a ScribeShadow price</span>
            </div>
            <p>
              Illustrative 6–12¢ per-source-word marketplace range for book
              translation. Rates vary by project.{" "}
              <a
                href={BOOK_TRANSLATION_RATES_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Book translation pricing source
              </a>
            </p>
          </aside>
          <p className="project-cost-estimator__note">
            Planning estimate only. Extracted word count, optional models, and
            reruns can change the final total.
          </p>
        </div>
      )}
    </section>
  );
};

export default ProjectCostEstimator;
